import { create } from 'zustand';
import {
  auth,
  firestore,
  signInWithPopup,
  signInWithCustomToken,
  getAdditionalUserInfo,
  twitterProvider,
  firebaseSignOut,
  onAuthStateChanged,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  type FirebaseUser
} from '../lib/firebase';
import type { Profile } from '../lib/firebase';
import { checkUsernameAvailable } from '../utils/validateUsername';

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  username?: string;
  allowMessages?: boolean;
  authProvider?: 'discord' | 'twitter';
  isAdmin?: boolean;
  isBanned?: boolean;
  banReason?: string;
}

interface ProfileUpdate {
  username?: string;
  allowMessages?: boolean;
  profilePicture?: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  profile: Profile | null;
  signInWithTwitter: () => Promise<void>;
  signInWithDiscord: () => void;
  completeDiscordSignIn: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: ProfileUpdate) => Promise<void>;
  getUserProfile: (userId: string) => Promise<Profile | null>;
  listFriendRequests: (userId: string) => Promise<any[]>;
  sendFriendRequest: (fromUserId: string, toUserId: string) => Promise<void>;
  acceptFriendRequest: (requestId: string) => Promise<void>;
  storeGameResult: (player1Id: string, player2Id: string, gameType: string, winnerId?: string, isDraw?: boolean) => Promise<void>;
  initializeAuth: () => Promise<void>;
}

// While an interactive sign-in is running, the onAuthStateChanged listener
// would otherwise race the profile creation. The sign-in method sets the
// authoritative state itself, so the listener stands down until it finishes.
let signInInProgress = false;

async function buildUserFromFirebase(firebaseUser: FirebaseUser, profile: Profile | null): Promise<User> {
  const isTwitter = firebaseUser.providerData.some(p => p.providerId === 'twitter.com');
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: profile?.username || firebaseUser.displayName || null,
    photoURL: profile?.avatar_url || null,
    username: profile?.username,
    allowMessages: profile?.allow_messages,
    authProvider: isTwitter ? 'twitter' : 'discord',
    isAdmin: profile?.is_admin === true,
    isBanned: profile?.is_banned === true,
    banReason: profile?.ban_reason,
  };
}

// Reduce a third-party handle to a valid 2Players username (3-20 chars,
// letters/numbers/underscores). Falls back to a generic name if nothing is left.
function sanitizeUsername(raw: string): string {
  let s = (raw || '').replace(/[^a-zA-Z0-9_]/g, '');
  if (s.length > 20) s = s.slice(0, 20);
  if (s.length < 3) s = (s + 'player').slice(0, 8);
  return s;
}

// Pick a free username, auto-suffixing with digits on collision (john -> john2).
async function resolveUniqueUsername(base: string): Promise<string> {
  const clean = sanitizeUsername(base);
  let candidate = clean;
  let suffix = 1;
  let attempts = 0;
  // checkUsernameAvailable returns a message string when taken, null when free.
  while ((await checkUsernameAvailable(candidate)) && attempts < 1000) {
    suffix += 1;
    attempts += 1;
    const tag = String(suffix);
    candidate = clean.slice(0, Math.max(1, 20 - tag.length)) + tag;
  }
  return candidate;
}

// Create the Firestore profile on first sign-in; return the existing one otherwise.
async function ensureProfile(firebaseUser: FirebaseUser, desiredUsername: string): Promise<Profile> {
  const profileRef = doc(firestore, 'profiles', firebaseUser.uid);
  const snap = await getDoc(profileRef);
  if (snap.exists()) {
    return { id: snap.id, ...snap.data() } as Profile;
  }
  const username = await resolveUniqueUsername(desiredUsername);
  const profileData = {
    username,
    email: firebaseUser.email || '',
    allow_messages: true,
    created_at: new Date().toISOString(),
    elo_rating: 1000,
  };
  await setDoc(profileRef, profileData);
  return { id: firebaseUser.uid, ...profileData } as Profile;
}

function friendlyError(error: unknown): string {
  const msg = (error as Error)?.message || 'Something went wrong. Please try again.';
  if (msg.includes('auth/popup-closed-by-user')) return 'Sign-in window was closed before completing.';
  if (msg.includes('auth/popup-blocked')) return 'Your browser blocked the sign-in popup. Allow popups and try again.';
  if (msg.includes('auth/cancelled-popup-request')) return 'Sign-in was cancelled.';
  if (msg.includes('auth/account-exists-with-different-credential')) {
    return 'An account already exists with this email using a different login method.';
  }
  return msg;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,
  profile: null,

  initializeAuth: async () => {
    set({ loading: true });
    // Firebase onAuthStateChanged fires immediately with cached state
    onAuthStateChanged(auth, async (firebaseUser) => {
      // An interactive sign-in is mid-flight and will set state itself.
      if (signInInProgress) return;
      if (firebaseUser) {
        const profile = await get().getUserProfile(firebaseUser.uid);
        const user = await buildUserFromFirebase(firebaseUser, profile);
        set({ user, profile, loading: false });
      } else {
        set({ user: null, profile: null, loading: false });
      }
    });
  },

  signInWithTwitter: async () => {
    set({ loading: true, error: null });
    signInInProgress = true;
    try {
      const result = await signInWithPopup(auth, twitterProvider);
      const info = getAdditionalUserInfo(result);
      const handle =
        info?.username ||
        (info?.profile as { screen_name?: string } | undefined)?.screen_name ||
        result.user.displayName ||
        'player';
      const profile = await ensureProfile(result.user, handle);
      const user = await buildUserFromFirebase(result.user, profile);
      set({ user, profile, loading: false });
    } catch (error) {
      set({ error: friendlyError(error), loading: false });
      throw error;
    } finally {
      signInInProgress = false;
    }
  },

  signInWithDiscord: () => {
    const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
    const redirectUri = import.meta.env.VITE_DISCORD_REDIRECT_URI;
    if (!clientId || !redirectUri) {
      set({ error: 'Discord login is not configured.' });
      return;
    }
    // CSRF state — verified by the callback route.
    const state = crypto.randomUUID();
    sessionStorage.setItem('discord_oauth_state', state);
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'identify',
      state,
    });
    window.location.href = `https://discord.com/api/oauth2/authorize?${params.toString()}`;
  },

  completeDiscordSignIn: async (code: string) => {
    set({ loading: true, error: null });
    signInInProgress = true;
    try {
      // The Cloudflare Pages Function exchanges the code and mints a token.
      const res = await fetch('/api/auth/discord', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Discord sign-in failed.');
      }
      const { token, username } = await res.json();

      const result = await signInWithCustomToken(auth, token);
      const profile = await ensureProfile(result.user, username || 'player');
      const user = await buildUserFromFirebase(result.user, profile);
      set({ user, profile, loading: false });
    } catch (error) {
      set({ error: friendlyError(error), loading: false });
      throw error;
    } finally {
      signInInProgress = false;
    }
  },

  signOut: async () => {
    set({ loading: true });
    try {
      await firebaseSignOut(auth);
      set({ user: null, profile: null, error: null, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  updateProfile: async (updates: ProfileUpdate) => {
    set({ loading: true, error: null });
    try {
      const { user } = get();
      if (!user) throw new Error('No user logged in');

      if (updates.username) {
        const taken = await checkUsernameAvailable(updates.username, user.uid);
        if (taken) throw new Error(taken);
      }

      const updateData: Record<string, any> = {};
      if (updates.username) updateData.username = updates.username;
      if (updates.allowMessages !== undefined) updateData.allow_messages = updates.allowMessages;
      if (updates.profilePicture) updateData.avatar_url = updates.profilePicture;

      await updateDoc(doc(firestore, 'profiles', user.uid), updateData);

      set(state => ({
        user: state.user ? {
          ...state.user,
          username: updates.username || state.user.username,
          allowMessages: updates.allowMessages !== undefined ? updates.allowMessages : state.user.allowMessages,
          photoURL: updates.profilePicture || state.user.photoURL,
        } : null
      }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  getUserProfile: async (userId: string): Promise<Profile | null> => {
    try {
      const snap = await getDoc(doc(firestore, 'profiles', userId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() } as Profile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  },

  listFriendRequests: async (userId: string) => {
    try {
      const q = query(
        collection(firestore, 'friendships'),
        where('friend_id', '==', userId),
        where('status', '==', 'pending')
      );
      const snap = await getDocs(q);
      const requests: any[] = [];

      for (const friendDoc of snap.docs) {
        const data = friendDoc.data();
        // Fetch sender's profile
        const senderProfile = await get().getUserProfile(data.user_id);
        requests.push({
          id: friendDoc.id,
          ...data,
          profiles: senderProfile ? { username: senderProfile.username, avatar_url: senderProfile.avatar_url } : null,
        });
      }

      return requests;
    } catch (error) {
      console.error('Error fetching friend requests:', error);
      return [];
    }
  },

  sendFriendRequest: async (fromUserId: string, toUserId: string) => {
    try {
      await addDoc(collection(firestore, 'friendships'), {
        user_id: fromUserId,
        friend_id: toUserId,
        status: 'pending',
        created_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error sending friend request:', error);
      throw error;
    }
  },

  acceptFriendRequest: async (requestId: string) => {
    try {
      await updateDoc(doc(firestore, 'friendships', requestId), { status: 'accepted' });
    } catch (error) {
      console.error('Error accepting friend request:', error);
      throw error;
    }
  },

  storeGameResult: async (player1Id: string, player2Id: string, gameType: string, winnerId?: string, isDraw = false) => {
    try {
      await addDoc(collection(firestore, 'gameResults'), {
        player1_id: player1Id,
        player2_id: player2Id,
        game_type: gameType,
        winner_id: winnerId || null,
        is_draw: isDraw,
        created_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error storing game result:', error);
      throw error;
    }
  },
}));
